/**
 * The exact icons the mobile app uses (Ionicons + MDI chef-hat), inlined as
 * SVG so they inherit currentColor. Sourced from ionicons/dist/svg and
 * @mdi/js (both MIT). Filled variants render when `filled` (active tab).
 */

export type AppIconName =
  | 'today'
  | 'chef-hat'
  | 'stats-chart'
  | 'settings'
  | 'add'
  | 'print'
  | 'trash'
  | 'create'
  | 'camera'
  | 'chevron-back'
  | 'chevron-forward'
  | 'flame'
  | 'snow'
  | 'time'
  | 'refresh'
  | 'heart'
  | 'restaurant'
  | 'pricetag'
  | 'cart'
  | 'alert-circle'
  | 'checkmark-circle'
  | 'bulb'
  | 'bulb-outline'
  | 'flask-outline'

const S = 32 // ionicons stroke width (512 viewBox)

export function AppIcon({
  name,
  size = 22,
  filled = false,
  className,
}: {
  name: AppIconName
  size?: number
  filled?: boolean
  className?: string
}) {
  const common = { width: size, height: size, className, 'aria-hidden': true as const }

  if (name === 'chef-hat') {
    return (
      <svg {...common} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.5,1.5C10.73,1.5 9.17,2.67 8.67,4.37C8.14,4.13 7.58,4 7,4A4,4 0 0,0 3,8C3,9.82 4.24,11.41 6,11.87V19H19V11.87C20.76,11.41 22,9.82 22,8A4,4 0 0,0 18,4C17.42,4 16.86,4.13 16.33,4.37C15.83,2.67 14.27,1.5 12.5,1.5M12,10.5H13V17.5H12V10.5M9,12.5H10V17.5H9V12.5M15,12.5H16V17.5H15V12.5M6,20V21A1,1 0 0,0 7,22H18A1,1 0 0,0 19,21V20H6Z" />
      </svg>
    )
  }

  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: S,
  }

  switch (name) {
    case 'today':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M416 64h-16V48.45c0-8.61-6.62-16-15.23-16.43A16 16 0 0 0 368 48v16H144V48.45c0-8.61-6.62-16-15.23-16.43A16 16 0 0 0 112 48v16H96a64 64 0 0 0-64 64v12a4 4 0 0 0 4 4h440a4 4 0 0 0 4-4v-12a64 64 0 0 0-64-64M477 176H35a3 3 0 0 0-3 3v237a64 64 0 0 0 64 64h320a64 64 0 0 0 64-64V179a3 3 0 0 0-3-3M224 307.43A28.57 28.57 0 0 1 195.43 336h-70.86A28.57 28.57 0 0 1 96 307.43v-70.86A28.57 28.57 0 0 1 124.57 208h70.86A28.57 28.57 0 0 1 224 236.57Z" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <rect {...stroke} width="416" height="384" x="48" y="80" rx="48" />
          <path {...stroke} d="M128 48v32M384 48v32" />
          <rect {...stroke} width="96" height="96" x="112" y="224" rx="13" />
          <path {...stroke} d="M464 160H48" />
        </svg>
      )
    case 'stats-chart':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M104 496H72a24 24 0 0 1-24-24V328a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v144a24 24 0 0 1-24 24M328 496h-32a24 24 0 0 1-24-24V232a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v240a24 24 0 0 1-24 24M440 496h-32a24 24 0 0 1-24-24V120a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v352a24 24 0 0 1-24 24M216 496h-32a24 24 0 0 1-24-24V40a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v432a24 24 0 0 1-24 24" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <rect {...stroke} width="48" height="160" x="64" y="320" rx="8" />
          <rect {...stroke} width="48" height="256" x="288" y="224" rx="8" />
          <rect {...stroke} width="48" height="368" x="400" y="112" rx="8" />
          <rect {...stroke} width="48" height="448" x="176" y="32" rx="8" />
        </svg>
      )
    case 'settings':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <circle cx="256" cy="256" r="48" />
          <path d="m470.39 300-.47-.38-31.56-24.75a16.11 16.11 0 0 1-6.1-13.33v-11.56a16 16 0 0 1 6.11-13.22L469.92 212l.47-.38a26.68 26.68 0 0 0 5.9-34.06l-42.71-73.9a1.6 1.6 0 0 1-.13-.22A26.86 26.86 0 0 0 401 92.14l-.35.13-37.1 14.93a15.94 15.94 0 0 1-14.47-1.29q-4.92-3.1-10-5.86a15.94 15.94 0 0 1-8.19-11.82l-5.59-39.59-.12-.72A27.22 27.22 0 0 0 298.76 26h-85.52a26.92 26.92 0 0 0-26.45 22.39l-.09.56-5.57 39.67a16 16 0 0 1-8.13 11.82 175 175 0 0 0-10 5.82 15.92 15.92 0 0 1-14.43 1.27l-37.13-15-.35-.14a26.87 26.87 0 0 0-32.48 11.34l-.13.22-42.77 73.95a26.71 26.71 0 0 0 5.9 34.1l.47.38 31.56 24.75a16.11 16.11 0 0 1 6.1 13.33v11.56a16 16 0 0 1-6.11 13.22L42.08 300l-.47.38a26.68 26.68 0 0 0-5.9 34.06l42.71 73.9a1.6 1.6 0 0 1 .13.22 26.86 26.86 0 0 0 32.45 11.3l.35-.13 37.07-14.93a15.94 15.94 0 0 1 14.47 1.29q4.92 3.11 10 5.86a15.94 15.94 0 0 1 8.19 11.82l5.56 39.59.12.72A27.22 27.22 0 0 0 213.24 486h85.52a26.92 26.92 0 0 0 26.45-22.39l.09-.56 5.57-39.67a16 16 0 0 1 8.18-11.82c3.42-1.84 6.76-3.79 10-5.82a15.92 15.92 0 0 1 14.43-1.27l37.13 14.95.35.14a26.85 26.85 0 0 0 32.48-11.34 3 3 0 0 1 .13-.22l42.71-73.89a26.7 26.7 0 0 0-5.89-34.11m-134.48-40.24a80 80 0 1 1-83.66-83.67 80.21 80.21 0 0 1 83.66 83.67" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M262.29 192.31a64 64 0 1 0 57.4 57.4 64.13 64.13 0 0 0-57.4-57.4M416.39 256a154 154 0 0 1-1.53 20.79l45.21 35.46a10.81 10.81 0 0 1 2.45 13.75l-42.77 74a10.81 10.81 0 0 1-13.14 4.59l-44.9-18.08a16.11 16.11 0 0 0-15.17 1.75A164.5 164.5 0 0 1 325 400.8a15.94 15.94 0 0 0-8.82 12.14l-6.73 47.89a11.08 11.08 0 0 1-10.68 9.17h-85.54a11.11 11.11 0 0 1-10.69-8.87l-6.72-47.82a16.07 16.07 0 0 0-9-12.22 155 155 0 0 1-21.46-12.57 16 16 0 0 0-15.11-1.71l-44.89 18.07a10.81 10.81 0 0 1-13.14-4.58l-42.77-74a10.8 10.8 0 0 1 2.45-13.75l38.21-30a16.05 16.05 0 0 0 6-14.08c-.36-4.17-.58-8.33-.58-12.5s.21-8.27.58-12.35a16 16 0 0 0-6.07-13.94l-38.19-30A10.81 10.81 0 0 1 49.48 186l42.77-74a10.81 10.81 0 0 1 13.14-4.59l44.9 18.08a16.11 16.11 0 0 0 15.17-1.75A164.5 164.5 0 0 1 187 111.2a15.94 15.94 0 0 0 8.82-12.14l6.73-47.89A11.08 11.08 0 0 1 213.23 42h85.54a11.11 11.11 0 0 1 10.69 8.87l6.72 47.82a16.07 16.07 0 0 0 9 12.22 155 155 0 0 1 21.46 12.57 16 16 0 0 0 15.11 1.71l44.89-18.07a10.81 10.81 0 0 1 13.14 4.58l42.77 74a10.8 10.8 0 0 1-2.45 13.75l-38.21 30a16.05 16.05 0 0 0-6.05 14.08c.33 4.14.55 8.3.55 12.47" />
        </svg>
      )
    case 'add':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M256 112v288M400 256H112" />
        </svg>
      )
    case 'print':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M384 368h24a40.12 40.12 0 0 0 40-40V168a40.12 40.12 0 0 0-40-40H104a40.12 40.12 0 0 0-40 40v160a40.12 40.12 0 0 0 40 40h24" />
          <rect {...stroke} width="256" height="208" x="128" y="240" rx="24.32" />
          <path {...stroke} d="M384 128v-24a40.12 40.12 0 0 0-40-40H168a40.12 40.12 0 0 0-40 40v24" />
          <circle cx="392" cy="184" r="24" fill="currentColor" />
        </svg>
      )
    case 'trash':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="m112 112 20 320c.95 18.49 14.4 32 32 32h184c17.67 0 30.87-13.51 32-32l20-320" />
          <path {...stroke} d="M80 112h352" />
          <path {...stroke} d="M192 112V72a23.93 23.93 0 0 1 24-24h80a23.93 23.93 0 0 1 24 24v40M256 176v224M184 176l8 224M328 176l-8 224" />
        </svg>
      )
    case 'create':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M384 224v184a40 40 0 0 1-40 40H104a40 40 0 0 1-40-40V168a40 40 0 0 1 40-40h167.48" />
          <path fill="currentColor" d="M459.94 53.25a16.06 16.06 0 0 0-23.22-.56L424.35 65a8 8 0 0 0 0 11.31l11.34 11.32a8 8 0 0 0 11.34 0l12.06-12c6.1-6.09 6.67-16.01.85-22.38M399.34 90 218.82 270.2a9 9 0 0 0-2.31 3.93L208.16 299a3.91 3.91 0 0 0 4.86 4.86l24.85-8.35a9 9 0 0 0 3.93-2.31L422 112.66a9 9 0 0 0 0-12.66l-9.95-10a9 9 0 0 0-12.71 0" />
        </svg>
      )
    case 'camera':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="m350.54 148.68-26.62-42.06C318.31 100.08 310.62 96 302 96h-92c-8.62 0-16.31 4.08-21.92 10.62l-26.62 42.06C155.85 155.23 148.62 160 140 160H80a32 32 0 0 0-32 32v192a32 32 0 0 0 32 32h352a32 32 0 0 0 32-32V192a32 32 0 0 0-32-32h-59c-8.65 0-16.85-4.77-22.46-11.32" />
          <circle cx="256" cy="272" r="80" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth={S} />
          <path {...stroke} d="M124 158v-22h-24v22" />
        </svg>
      )
    case 'chevron-back':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={48} d="M328 112 184 256l144 144" />
        </svg>
      )
    case 'chevron-forward':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={48} d="m184 112 144 144-144 144" />
        </svg>
      )
    case 'flame':
      return (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M394.23 197.56a300.43 300.43 0 00-53.37-90C301.2 61.65 249.05 32 208 32a16 16 0 00-15.48 20c13.87 53-14.88 97.07-45.31 143.72C122 234.36 96 274.27 96 320c0 88.22 71.78 160 160 160s160-71.78 160-160c0-43.3-7.32-84.49-21.77-122.44zm-105.9 221.13C278 429.69 265.05 432 256 432s-22-2.31-32.33-13.31S208 390.24 208 368c0-25.14 8.82-44.28 17.34-62.78 4.95-10.74 10-21.67 13-33.37a8 8 0 0112.49-4.51A126.48 126.48 0 01275 292c18.17 24 29 52.42 29 76 0 22.24-5.42 39.77-15.67 50.69z" />
        </svg>
      )
    case 'snow':
      // Streak-freeze indicator — ionicons snow-outline, sourced the same way as
      // the other icons in this file (see file header comment).
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path
            {...stroke}
            d="M256 32v448M313.72 80A111.47 111.47 0 0 1 256 96a111.47 111.47 0 0 1-57.72-16M198.28 432a112.11 112.11 0 0 1 115.44 0M449.99 144 62.01 368M437.27 218a112.09 112.09 0 0 1-57.71-100M74.73 294a112.09 112.09 0 0 1 57.71 100M62.01 144l387.98 224M74.73 218a112.09 112.09 0 0 0 57.71-100M437.27 294a112.09 112.09 0 0 0-57.71 100"
          />
        </svg>
      )
    case 'time':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64Z" />
          <path {...stroke} d="M256 128v144h96" />
        </svg>
      )
    case 'refresh':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M320 146s24.36-12-64-12a160 160 0 10160 160" />
          <path {...stroke} d="m256 58 80 80-80 80" />
        </svg>
      )
    case 'restaurant':
      // Photo-less saved-food placeholder — mobile uses the same Ionicons glyph
      // (restaurant-outline) tinted by safety color; ⭐ here read as "favorited".
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={S} d="m57.49 47.74 368.43 368.43a37.28 37.28 0 0 1 0 52.72 37.29 37.29 0 0 1-52.72 0l-90-91.55a32 32 0 0 1-9.2-22.43v-5.53a32 32 0 0 0-9.52-22.78l-11.62-10.73a32 32 0 0 0-29.8-7.44 48.53 48.53 0 0 1-46.56-12.63l-85.43-85.44C40.39 159.68 21.74 83.15 57.49 47.74Z" />
          <path {...stroke} d="m400 32-77.25 77.25A64 64 0 0 0 304 154.51v14.86a16 16 0 0 1-4.69 11.32L288 192M320 224l11.31-11.31a16 16 0 0 1 11.32-4.69h14.86a64 64 0 0 0 45.26-18.75L480 112M440 72l-80 80M200 368l-99.72 100.28a40 40 0 0 1-56.56 0h0a40 40 0 0 1 0-56.56L128 328" />
        </svg>
      )
    case 'pricetag':
      // Meal-time tag editor — mobile uses the same Ionicons glyph
      // (pricetag-outline) on its tag action.
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M435.25 48h-122.9a14.46 14.46 0 0 0-10.2 4.2L56.45 297.9a28.85 28.85 0 0 0 0 40.7l117 117a28.86 28.86 0 0 0 40.7 0L459.75 210a14.46 14.46 0 0 0 4.2-10.2v-123a28.66 28.66 0 0 0-28.7-28.8Z" />
          <circle cx="384" cy="128" r="32" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth={S} />
        </svg>
      )
    case 'cart':
      // Shopping list — mobile uses the same Ionicons glyph (cart / cart-outline).
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <circle cx="176" cy="416" r="32" />
          <circle cx="400" cy="416" r="32" />
          <path d="M456.8 120.78a23.92 23.92 0 0 0-18.56-8.78H133.89l-6.13-34.78A16 16 0 0 0 112 64H48a16 16 0 0 0 0 32h50.58l45.66 258.78A16 16 0 0 0 160 368h256a16 16 0 0 0 0-32H173.42l-5.64-32h241.66a24.07 24.07 0 0 0 23.62-19.54l28.62-149.6a24 24 0 0 0-4.88-14.08z" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <circle cx="176" cy="416" r="16" {...stroke} />
          <circle cx="400" cy="416" r="16" {...stroke} />
          <path {...stroke} d="M48 80h64l48 272h256" />
          <path {...stroke} d="M160 288h249.44a8 8 0 0 0 7.85-6.43l28.8-144a8 8 0 0 0-7.85-9.57H128" />
        </svg>
      )
    case 'alert-circle':
      return (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48m0 319.91a20 20 0 1 1 20-20 20 20 0 0 1-20 20m21.72-201.15-5.74 122a16 16 0 0 1-32 0l-5.74-121.94v-.05a21.74 21.74 0 1 1 43.44 0Z" />
        </svg>
      )
    case 'checkmark-circle':
      return (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48m108.25 138.29-134.4 160a16 16 0 0 1-12 5.71h-.27a16 16 0 0 1-11.89-5.3l-57.6-64a16 16 0 1 1 23.78-21.4l45.29 50.32 122.59-145.91a16 16 0 0 1 24.5 20.58" />
        </svg>
      )
    case 'bulb':
      return (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M288 464h-64a16 16 0 0 0 0 32h64a16 16 0 0 0 0-32M304 416h-96a16 16 0 0 0 0 32h96a16 16 0 0 0 0-32M369.42 62.69C339.35 32.58 299.07 16 256 16A159.62 159.62 0 0 0 96 176c0 46.62 17.87 90.23 49 119.64l4.36 4.09C167.37 316.57 192 339.64 192 360v24a16 16 0 0 0 16 16h24a8 8 0 0 0 8-8V274.82a8 8 0 0 0-5.13-7.47A130.7 130.7 0 0 1 208.71 253a16 16 0 1 1 18.58-26c7.4 5.24 21.65 13 28.71 13s21.31-7.78 28.73-13a16 16 0 0 1 18.56 26 130.7 130.7 0 0 1-26.16 14.32 8 8 0 0 0-5.13 7.47V392a8 8 0 0 0 8 8h24a16 16 0 0 0 16-16v-24c0-19.88 24.36-42.93 42.15-59.77l4.91-4.66C399.08 265 416 223.61 416 176a159.16 159.16 0 0 0-46.58-113.31" />
        </svg>
      )
    case 'bulb-outline':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M304 384v-24c0-29 31.54-56.43 52-76 28.84-27.57 44-64.61 44-108 0-80-63.73-144-144-144a143.6 143.6 0 0 0-144 144c0 41.84 15.81 81.39 44 108 20.35 19.21 52 46.7 52 76v24M224 480h64M208 432h96M256 384V256" />
          <path {...stroke} d="M294 240s-21.51 16-38 16-38-16-38-16" />
        </svg>
      )
    case 'flask-outline':
      // 4.39 K4c: acid load (PRAL) — the Settings / PralLine icon on mobile.
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeMiterlimit={10}
            strokeWidth={S}
            d="M176 48h160M118 304h276M208 48v93.48a64.1 64.1 0 0 1-9.88 34.18L73.21 373.49C48.4 412.78 76.63 464 123.08 464h265.84c46.45 0 74.68-51.22 49.87-90.51L313.87 175.66a64.1 64.1 0 0 1-9.87-34.18V48"
          />
        </svg>
      )
    case 'heart':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M256 448a32 32 0 0 1-18-5.57c-78.59-53.35-112.62-89.93-131.39-112.8-44.7-54.55-67.29-111.9-67.29-170.42C39.32 96.61 84.72 48 145.68 48c39.36 0 76.26 22.14 96.66 57.44a4.62 4.62 0 0 0 7.32 0C269.72 70.14 306.62 48 346 48h.35c60.94 0 106.34 48.61 106.34 111.21 0 58.52-22.59 115.87-67.29 170.42-18.77 22.87-52.8 59.45-131.39 112.8a32 32 0 0 1-18.01 5.57z" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M352.92 80C288 80 256 144 256 144s-32-64-96.92-64C106.51 80 64.44 124.14 64 176.81c-.53 62.5 27.79 137.02 233.62 251.16a12 12 0 0 0 8.76 0C512.21 313.83 540.53 239.31 480 176.81 439.56 124.14 397.49 80 352.92 80z" />
        </svg>
      )
  }
}
